import { useId, type JSX } from 'react';

import { Label } from '../../primitives/label';
import { LoadingAddon } from '../../primitives/loading-addon';
import { RadioGroup, RadioGroupItem } from '../../primitives/radio-group';
import { Skeleton } from '../../primitives/skeleton';
import { cn } from '../../utils/cn';
import { OptionInfoField, OptionInfoPopover } from '../option-info-popover';

export type RadioGroupModelModel = {
  infoFields?: OptionInfoField[];
  value: string;
  item: string | JSX.Element;
}[];

interface RadioGroupModelProps extends React.ComponentPropsWithoutRef<typeof RadioGroup> {
  disabledOnlyStyles?: boolean;
  isLoading?: boolean;
  model: RadioGroupModelModel;
  type?: 'group' | 'single';
}

/**
 * @param disabledOnlyStyles **optional** Apply disabled styles without functionality. Helps when you are using react-hook-form
 * and the disabled functionality is on the fieldset Ref: https://react-hook-form.com/docs/useform/handlesubmit
 * @param model Data model with all the available options
 * @param type **optional** `group` by default. Groups al radio items in a card or one card for each radio item.
 * Helps to add styles, if you need more styles add them with className
 */
export function RadioGroupModel({
  className,
  disabled,
  disabledOnlyStyles = false,
  isLoading = false,
  model,
  type = 'group',
  ...props
}: RadioGroupModelProps) {
  const baseId = useId(); // avoids collision between components with the same label value

  const hasValues = model.some((item) => {
    const value = item?.value;
    return typeof value === 'string' ? value.trim() !== '' : value != null;
  });

  return (
    <RadioGroup
      {...props}
      disabled={disabled || model.length === 0 || isLoading}
      className={cn(
        'relative',
        {
          'gap-0 rounded-lg border border-input shadow-xs': type === 'group',
          'cursor-not-allowed border-input/50': disabled || disabledOnlyStyles || isLoading,
        },
        className,
      )}
    >
      {hasValues ? (
        model.map((modelItem) => {
          if (!modelItem?.value) return null;

          const id = `${baseId}-radio-${modelItem.value}`;

          return (
            <Label id={props?.id ? `${props.id}-${id}` : undefined} htmlFor={id} key={id}>
              <div
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg py-2 text-base hover:bg-accent',
                  {
                    'border border-input px-4': type === 'single',
                    'm-[2px] px-4': type === 'group',
                    'text-muted-foreground hover:cursor-not-allowed hover:bg-transparent':
                      disabled || disabledOnlyStyles || isLoading,
                  },
                )}
              >
                <RadioGroupItem value={modelItem.value} id={id} />
                <div className="inline-flex items-center gap-1">
                  {modelItem.item}
                  <OptionInfoPopover fields={modelItem.infoFields} />
                </div>
              </div>
            </Label>
          );
        })
      ) : (
        <div className="flex h-14 items-center px-4">
          {model.length !== 0 && !hasValues ? (
            <p className="text-base text-muted-foreground">
              La fuente de datos no contiene valores válidos
            </p>
          ) : (
            <Skeleton className="h-8 w-32" />
          )}
        </div>
      )}
      {isLoading && <LoadingAddon type="plain" />}
    </RadioGroup>
  );
}
